import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Select,
  useToast,
  useColorModeValue,
  Container,
  Heading,
  Divider,
  Card,
  CardBody,
  Badge,
  IconButton,
  SimpleGrid,
  Spinner,
  Center
} from '@chakra-ui/react';
import { FiArrowLeft, FiEdit, FiTrash2, FiArrowUp, FiArrowDown, FiPlay } from 'react-icons/fi';
import IconWrapper from '../components/IconWrapper';
import { useNavigate, useParams } from 'react-router-dom';

interface Episode {
  id: string;
  title: string;
  description: string;
  videoUrl: string;
  thumbnailUrl?: string;
  duration: number;
  order: number;
  uploadDate: string;
}

interface Season {
  id: string;
  title: string;
  description: string;
  episodes: Episode[];
  episodeCount: number;
}

interface Series {
  id: string;
  title: string;
  description: string;
  seasons: Season[];
  totalEpisodes: number;
}

const SeriesManagement: React.FC = () => {
  const { seriesId } = useParams<{ seriesId: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const [series, setSeries] = useState<Series | null>(null);
  const [selectedSeasonId, setSelectedSeasonId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const bgColor = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.900', 'white');
  const labelColor = useColorModeValue('gray.600', 'gray.400');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const cardBg = useColorModeValue('white', 'gray.700');

  useEffect(() => {
    if (seriesId) {
      loadSeries(seriesId);
    }
  }, [seriesId]);

  const loadSeries = async (id: string) => {
    try {
      setLoading(true);
      // TODO: Replace with actual API call
      const mockSeries: Series = {
        id: id,
        title: 'Breaking Bad',
        description: 'A high school chemistry teacher turned methamphetamine manufacturer',
        totalEpisodes: 62,
        seasons: [
          {
            id: 'season-1',
            title: 'Season 1',
            description: 'The beginning of Walter White\'s transformation',
            episodeCount: 7,
            episodes: [
              {
                id: 'ep-1-1',
                title: 'Pilot',
                description: 'Walter White discovers he has lung cancer',
                videoUrl: 'https://example.com/video1.mp4',
                thumbnailUrl: 'https://example.com/thumb1.jpg',
                duration: 58,
                order: 1,
                uploadDate: '2024-01-15'
              },
              {
                id: 'ep-1-2',
                title: 'Cat\'s in the Bag...',
                description: 'Walter and Jesse try to dispose of a body',
                videoUrl: 'https://example.com/video2.mp4',
                thumbnailUrl: 'https://example.com/thumb2.jpg',
                duration: 48,
                order: 2,
                uploadDate: '2024-01-16'
              },
              {
                id: 'ep-1-3',
                title: '...And the Bag\'s in the River',
                description: 'Walter makes a difficult decision',
                videoUrl: 'https://example.com/video3.mp4',
                thumbnailUrl: 'https://example.com/thumb3.jpg',
                duration: 50,
                order: 3,
                uploadDate: '2024-01-17'
              }
            ]
          },
          {
            id: 'season-2',
            title: 'Season 2',
            description: 'Walter\'s business expands',
            episodeCount: 13,
            episodes: [
              {
                id: 'ep-2-1',
                title: 'Seven Thirty-Seven',
                description: 'Walter faces new challenges',
                videoUrl: 'https://example.com/video4.mp4',
                thumbnailUrl: 'https://example.com/thumb4.jpg',
                duration: 47,
                order: 1,
                uploadDate: '2024-01-20'
              }
            ]
          }
        ]
      };
      setSeries(mockSeries);
      if (mockSeries.seasons.length > 0) {
        setSelectedSeasonId(mockSeries.seasons[0].id);
      }
    } catch (error) {
      console.error('Error loading series:', error);
      toast({
        title: 'Error',
        description: 'Failed to load series data',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const selectedSeason = series?.seasons.find(s => s.id === selectedSeasonId);

  const moveEpisode = (episodeId: string, direction: 'up' | 'down') => {
    if (!selectedSeason) return;

    const episodes = [...selectedSeason.episodes];
    const currentIndex = episodes.findIndex(ep => ep.id === episodeId);
    
    if (currentIndex === -1) return;

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    
    if (newIndex < 0 || newIndex >= episodes.length) return;

    // Swap episodes
    [episodes[currentIndex], episodes[newIndex]] = [episodes[newIndex], episodes[currentIndex]];
    
    // Update order numbers
    episodes.forEach((ep, index) => {
      ep.order = index + 1;
    });

    // Update series state
    setSeries(prev => {
      if (!prev) return null;
      return {
        ...prev,
        seasons: prev.seasons.map(season => 
          season.id === selectedSeasonId 
            ? { ...season, episodes }
            : season
        )
      };
    });
  };

  const saveEpisodeOrder = async () => {
    if (!selectedSeason) return;

    try {
      setSaving(true);
      // TODO: Implement actual API call to save episode order
      toast({
        title: 'Order Saved',
        description: 'Episode order has been updated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error saving episode order:', error);
      toast({
        title: 'Error',
        description: 'Failed to save episode order',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  if (loading) {
    return (
      <Container maxW="container.lg" py={8}>
        <Center minH="400px">
          <VStack gap={4}>
            <Spinner size="xl" color="purple.500" />
            <Text color={labelColor}>Loading series...</Text>
          </VStack>
        </Center>
      </Container>
    );
  }

  if (!series) {
    return (
      <Container maxW="container.lg" py={8}>
        <Center minH="400px">
          <VStack gap={4}>
            <Text fontSize="xl" color={textColor}>Series not found</Text>
            <Button onClick={() => navigate(-1)}>Go Back</Button>
          </VStack>
        </Center>
      </Container>
    );
  }

  return (
    <Container maxW="container.lg" py={8}>
      <VStack gap={6} align="stretch">
        {/* Header */}
        <HStack gap={4} align="center">
          <IconButton
            aria-label="Go back"
            icon={<IconWrapper icon={FiArrowLeft} size={20} />}
            variant="ghost"
            onClick={() => navigate(-1)}
          />
          <VStack align="start" gap={1}>
            <Heading size="lg" color={textColor}>
              {series.title}
            </Heading>
            <Text color={labelColor} fontSize="sm">
              {series.description}
            </Text>
          </VStack>
        </HStack>

        <Divider />

        {/* Season Selector */}
        <Box>
          <Text color={labelColor} fontWeight="bold" mb={2}>
            Select Season to Manage
          </Text>
          <Select
            value={selectedSeasonId || ''}
            onChange={(e) => setSelectedSeasonId(e.target.value || null)}
            bg={bgColor}
            borderColor={borderColor}
            maxW="300px"
          >
            {series.seasons.map((season) => (
              <option key={season.id} value={season.id}>
                {season.title} ({season.episodeCount} episodes)
              </option>
            ))}
          </Select>
        </Box>

        {/* Episodes List */}
        {selectedSeason && (
          <Box>
            <HStack justify="space-between" align="center" mb={4}>
              <VStack align="start" gap={1}>
                <Heading size="md" color={textColor}>
                  {selectedSeason.title}
                </Heading>
                <Text color={labelColor} fontSize="sm">
                  {selectedSeason.description}
                </Text>
              </VStack>
              <Button
                colorScheme="purple"
                onClick={saveEpisodeOrder}
                isLoading={saving}
                loadingText="Saving..."
              >
                Save Order
              </Button>
            </HStack>

            <VStack gap={3} align="stretch">
              {selectedSeason.episodes
                .sort((a, b) => a.order - b.order)
                .map((episode, index) => (
                <Card key={episode.id} bg={cardBg} borderColor={borderColor}>
                  <CardBody p={4}>
                    <HStack gap={4} align="center">
                      {/* Episode Number */}
                      <Box
                        minW="40px"
                        h="40px"
                        bg="purple.500"
                        color="white"
                        borderRadius="full"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        fontWeight="bold"
                        fontSize="sm"
                      >
                        {episode.order}
                      </Box>

                      {/* Episode Info */}
                      <VStack align="start" gap={1} flex={1}>
                        <HStack gap={2} align="center">
                          <Text fontWeight="bold" color={textColor}>
                            {episode.title}
                          </Text>
                          <Badge colorScheme="purple" variant="subtle">
                            {formatDuration(episode.duration)}
                          </Badge>
                        </HStack>
                        <Text color={labelColor} fontSize="sm" noOfLines={2}>
                          {episode.description}
                        </Text>
                        <Text color={labelColor} fontSize="xs">
                          Uploaded: {new Date(episode.uploadDate).toLocaleDateString()}
                        </Text>
                      </VStack>

                      {/* Actions */}
                      <HStack gap={2}>
                        <IconButton
                          aria-label="Move up"
                          icon={<IconWrapper icon={FiArrowUp} size={16} />}
                          size="sm"
                          variant="outline"
                          onClick={() => moveEpisode(episode.id, 'up')}
                          isDisabled={index === 0}
                        />
                        <IconButton
                          aria-label="Move down"
                          icon={<IconWrapper icon={FiArrowDown} size={16} />}
                          size="sm"
                          variant="outline"
                          onClick={() => moveEpisode(episode.id, 'down')}
                          isDisabled={index === selectedSeason.episodes.length - 1}
                        />
                        <IconButton
                          aria-label="Play episode"
                          icon={<IconWrapper icon={FiPlay} size={16} />}
                          size="sm"
                          colorScheme="purple"
                          variant="outline"
                          onClick={() => navigate(`/player/${episode.id}`)}
                        />
                        <IconButton
                          aria-label="Edit episode"
                          icon={<IconWrapper icon={FiEdit} size={16} />}
                          size="sm"
                          variant="outline"
                          onClick={() => navigate(`/episode/${episode.id}/edit`)}
                        />
                        <IconButton
                          aria-label="Delete episode"
                          icon={<IconWrapper icon={FiTrash2} size={16} />}
                          size="sm"
                          colorScheme="red"
                          variant="outline"
                          onClick={() => {
                            // TODO: Implement delete episode
                          }}
                        />
                      </HStack>
                    </HStack>
                  </CardBody>
                </Card>
              ))}
            </VStack>

            {selectedSeason.episodes.length === 0 && (
              <Box textAlign="center" py={8}>
                <Text color={labelColor}>No episodes in this season yet</Text>
                <Button
                  colorScheme="purple"
                  variant="outline"
                  mt={4}
                  onClick={() => navigate('/upload/video')}
                >
                  Upload First Episode
                </Button>
              </Box>
            )}
          </Box>
        )}
      </VStack>
    </Container>
  );
};

export default SeriesManagement;
